import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import authService from '../services/auth'
import AlertService from '../utils/alert'

/**
 * Composable para manejar el control de acceso en las vistas
 * @param {Object} options - Opciones de configuración
 * @param {boolean} options.requiresAuth - Si requiere autenticación (por defecto: true)
 * @param {boolean} options.requiresMenuPermission - Si requiere permisos de menú (por defecto: true)
 * @returns {Object} - Objeto con métodos y propiedades reactivas
 */
export function useAccessControl(options = {}) {
  const router = useRouter()
  
  // Configuración por defecto
  const config = {
    requiresAuth: options.requiresAuth !== undefined ? options.requiresAuth : true,
    requiresMenuPermission: options.requiresMenuPermission !== undefined ? options.requiresMenuPermission : true
  }
  
  // Estados reactivos
  const isAuthenticated = ref(false)
  const hasMenuPermission = ref(false)
  const isLoading = ref(true)
  const currentUser = ref(null)
  const userMenu = ref([])
  
  /**
   * Verificar si el usuario está autenticado
   */
  const checkAuthentication = () => {
    const isAuth = authService.isAuthenticated()
    console.log('[DEBUG] checkAuthentication:', isAuth)
    isAuthenticated.value = isAuth
    if (isAuthenticated.value) {
      currentUser.value = authService.getUser()
      userMenu.value = authService.getUserMenu() || []
    }
  }
  
  /**
   * Verificar si la ruta actual está en el menú del usuario
   */
  const checkMenuPermission = () => {
    if (!config.requiresMenuPermission) {
      hasMenuPermission.value = true
      return
    }
    
    const currentRoute = router.currentRoute.value.path
    console.log('[DEBUG] checkMenuPermission - currentRoute:', currentRoute)
    
    // Si no hay menú, denegar acceso
    if (!userMenu.value || userMenu.value.length === 0) {
      console.log('[DEBUG] checkMenuPermission - No userMenu or empty')
      hasMenuPermission.value = false
      return
    }
    
    console.log('[DEBUG] checkMenuPermission - userMenu:', userMenu.value)
    
    // Buscar la ruta en el menú del usuario
    const hasPermission = userMenu.value.some(menuItem => {
      console.log('[DEBUG] checkMenuPermission - Checking menuItem:', menuItem)
      
      // Verificar coincidencia exacta
      if (menuItem.Enlace === currentRoute) {
        console.log('[DEBUG] checkMenuPermission - Exact match found')
        return true
      }
      
      // Verificar si la ruta actual es una subruta del menú
      if (currentRoute.startsWith(menuItem.Enlace + '/')) {
        console.log('[DEBUG] checkMenuPermission - Subroute match found')
        return true
      }
      
      // Verificar patrones comunes de rutas
      const menuPath = menuItem.Enlace.replace(/\/$/, '') // Remover slash final
      const currentPath = currentRoute.replace(/\/$/, '') // Remover slash final
      
      if (menuPath === currentPath) {
        console.log('[DEBUG] checkMenuPermission - Pattern match found')
        return true
      }
      
      return false
    })
    
    console.log('[DEBUG] checkMenuPermission - Final hasPermission:', hasPermission)
    hasMenuPermission.value = hasPermission
  }
  
  /**
   * Verificar todos los permisos de acceso
   */
  const checkAccess = async () => {
    console.log('[DEBUG] checkAccess - Starting access check')
    isLoading.value = true
    
    try {
      // 1. Verificar autenticación si es requerida
      if (config.requiresAuth) {
        console.log('[DEBUG] checkAccess - Checking authentication')
        checkAuthentication()
        
        if (!isAuthenticated.value) {
          console.log('[DEBUG] checkAccess - Authentication failed')
          await AlertService.warning('Acceso denegado', 'Debe iniciar sesión para acceder a esta página')
          router.push('/login')
          return false
        }
      }
      
      // 2. Verificar permisos de menú si es requerido
      if (config.requiresMenuPermission) {
        console.log('[DEBUG] checkAccess - Checking menu permission')
        checkMenuPermission()
        
        if (!hasMenuPermission.value) {
          console.log('[DEBUG] checkAccess - Menu permission failed')
          await AlertService.error('Acceso denegado', 'No tiene permisos para acceder a esta página')
          router.push('/')
          return false
        }
      }
      
      console.log('[DEBUG] checkAccess - Access granted')
      return true
    } catch (error) {
      console.error('Error al verificar acceso:', error)
      await AlertService.error('Error', 'Error al verificar permisos de acceso')
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Propiedades computadas
   */
  const canAccess = computed(() => {
    if (isLoading.value) return false
    
    let authCheck = true
    let menuCheck = true
    
    if (config.requiresAuth) {
      authCheck = isAuthenticated.value
    }
    
    if (config.requiresMenuPermission) {
      menuCheck = hasMenuPermission.value
    }
    
    return authCheck && menuCheck
  })
  
  /**
   * Inicializar verificaciones al montar el componente
   */
  onMounted(async () => {
    await checkAccess()
  })
  
  return {
    // Estados
    isAuthenticated,
    hasMenuPermission,
    isLoading,
    currentUser,
    userMenu,
    canAccess,
    
    // Configuración
    config,
    
    // Métodos
    checkAuthentication,
    checkMenuPermission,
    checkAccess
  }
}

/**
 * Función helper para usar en router guards
 * @param {Object} to - Ruta de destino
 * @param {Object} options - Opciones de configuración
 * @returns {boolean} - true si tiene acceso, false en caso contrario
 */
export async function checkRouteAccess(to, options = {}) {
  const config = {
    requiresAuth: options.requiresAuth !== undefined ? options.requiresAuth : true,
    requiresMenuPermission: options.requiresMenuPermission !== undefined ? options.requiresMenuPermission : true
  }
  
  // Verificar autenticación
  if (config.requiresAuth && !authService.isAuthenticated()) {
    return false
  }
  
  // Verificar permisos de menú
  if (config.requiresMenuPermission) {
    const userMenu = authService.getUserMenu() || []
    const hasPermission = userMenu.some(menuItem => {
      return menuItem.Enlace === to.path || to.path.startsWith(menuItem.Enlace + '/')
    })
    
    if (!hasPermission) {
      return false
    }
  }
  
  return true
}

export default useAccessControl